import type {
  ApproveApplicationRequest,
  BrandApplication,
} from '@nucleus-commerce/api-client/platform';

import type { ApproveInput, ApproveOutput } from '../schemas/approve-schema';

/** The backend's default when the approval names none. */
export const DEFAULT_TIMEZONE = 'America/New_York';

/** Time zones the approve dialog offers; a store can change its own later. */
export const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
  'UTC',
] as const;

const COUNTRY_TIMEZONE: Readonly<Record<string, string>> = {
  US: 'America/New_York',
  CA: 'America/Toronto',
  MX: 'America/Mexico_City',
  BR: 'America/Sao_Paulo',
  GB: 'Europe/London',
  IE: 'Europe/Dublin',
  FR: 'Europe/Paris',
  DE: 'Europe/Berlin',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam',
  CH: 'Europe/Zurich',
  ZA: 'Africa/Johannesburg',
  AE: 'Asia/Dubai',
  IN: 'Asia/Kolkata',
  SG: 'Asia/Singapore',
  HK: 'Asia/Hong_Kong',
  JP: 'Asia/Tokyo',
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
};

/** Lowercase letters, digits and single hyphens, the way the backend spells a slug. */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
    .replace(/-+$/, '');
}

/** The approve form prefilled from the application: its handle, brand, currency and country. */
export function approveFormFor(application: BrandApplication): ApproveInput {
  return {
    slug: application.desiredSlug ?? slugify(application.brandName),
    storeName: application.brandName,
    currency: application.currency ?? '',
    country: application.countryCode,
    timezone: COUNTRY_TIMEZONE[application.countryCode] ?? DEFAULT_TIMEZONE,
    sendInvite: true,
  };
}

/** Form → DTO. Blank optional fields are omitted so the backend applies its defaults. */
export function toApproveRequest(values: ApproveOutput): ApproveApplicationRequest {
  return {
    slug: values.slug,
    ...(values.storeName ? { storeName: values.storeName } : {}),
    ...(values.currency ? { currency: values.currency } : {}),
    ...(values.country ? { country: values.country } : {}),
    timezone: values.timezone,
    sendInvite: values.sendInvite,
  };
}
